import { ColProps, UploadFile } from 'antd';
import { RcFile } from 'antd/es/upload';
import isSvg from 'is-svg';
import jsonpath from 'jsonpath';
import moment, { Moment } from 'moment';
import { DEFAULT_FORMAT_DATETIME_FORM, DEFAULT_FORMAT_DATE_FORM } from '../lib';
import { CompositorContextModel } from './components/CompositorProvider';
import { DEFAULT_FULL_COL_PROPS, DEFAULT_HALF_COL_PROPS } from './constants';
import { JSelectOption, Output } from './models';
import { ComponentConfigExtended, FieldDTOItemPayload, FieldDTOPayload, JComponentConfigOption, JFieldDTO, JFieldDTOType } from './models/overrideBE';

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item: any): item is Record<string, any> {
    return (item && typeof item === 'object' && !Array.isArray(item));
}

/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 */
export function mergeDeep(target: any, ...sources: any): Record<string, any> {
    if (!sources.length) return target;
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                mergeDeep(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }

    return mergeDeep(target, ...sources);
} interface Node {
    cmsConfig: {
        order: number,
    }
    children?: Node[]

}
export function recursiveSort<T extends Node>(array: T[]): void {
    array.sort((a, b) => {
        if (a?.children) {
            recursiveSort(a.children)
        }
        return a.cmsConfig.order - b.cmsConfig.order
    })
}

export function toTitleCase(str: string) {
    return str.replace(
        /\w\S*/g,
        function (txt) {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        }
    );
}


export function isValue(value: any): value is string[] {
    return Array.isArray(value)
}


export function cmsOptionsToSelectOptions(options: JComponentConfigOption[] = []): JSelectOption[] {
    return options.map(o => ({
        value: o.value,
        dependsValue: o.dependsValue,
        svg: isSvg(o.label.text || '') ? o.label.text : undefined,
        label: o.label.title
    }))
}
export function cmsOptionsToRadioOptions(options: JComponentConfigOption[] = []) {
    return options.map(o => ({
        value: o.value,
        label: o.label.title,
        imageURL: o.label.imageUrl
    }))
}

export function getColConfigFromFieldDTO(field: JFieldDTO): ColProps {
    switch (true) {
        case field.confirmed:
        case !!field.children?.length:
        case isFieldType.RADIO(field.type):
        case isFieldType.PRIVACY(field.type):
        case isFieldType.CHECKBOX(field.type):
        case isFieldType.MULTISELECT(field.type):
        case isFieldType.TEXTAREA(field.type):
            return DEFAULT_FULL_COL_PROPS
        default:
            return DEFAULT_HALF_COL_PROPS
    }
}
export function isUploadFile(value: any): value is UploadFile {
    return (
        !!value &&
        !!value.uid &&
        !!value.name &&
        !!value.percent !== undefined &&
        !!value.size !== undefined &&
        !!value.lastModified !== undefined
    )
}
export function isUploadValue(value: any): value is UploadFile[] {
    return (
        Array.isArray(value) &&
        !!value?.[0] &&
        isUploadFile(value[0])
    )

}
export function isRepeatableUploadValue(value: any): value is UploadFile[][] {
    return Array.isArray(value) && Array.isArray(value?.[0]) && isUploadValue(value[0])
}
export const urlToObject = async (url: string, filename: string) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
}
interface OnFinishConfig {
    fields: JFieldDTO[],
    data: Output,
    uploadFiles?: CompositorContextModel['uploadFiles']
}
export const onFinishForm = async (config: OnFinishConfig) => {
    try {

        const payload = buildFieldsPayload(config.data, config.fields)

        const uploadMap = getUploadMap(payload, config.fields)

        await applyUploadMap(payload, uploadMap, config.uploadFiles)

        return payload
    } catch (error) {
        console.error(error)
        throw error
    }
}
const applyUploadMap = async (payload: FieldDTOPayload, map: Record<string, UploadFile | string>, uploadFiles?: CompositorContextModel['uploadFiles']) => {
    if (uploadFiles) {
        const notUploadedFiles: Record<string, UploadFile> = {}

        for (const [key, value] of Object.entries(map)) {
            if (isUploadFile(value)) {
                notUploadedFiles[key] = value
            }
            if (typeof value === 'string') {
                jsonpath.value(payload, key, value)
            }
        }
        try {

            const uploadResponses = await uploadFiles(Object.values(notUploadedFiles).map(u => u.originFileObj as RcFile))
            const mapKeys = Object.keys(notUploadedFiles)
            const mapValues = Object.values(notUploadedFiles)

            for (let i = 0; i < uploadResponses.length; i++) {
                const uploadResponse = uploadResponses[i];
                if (uploadResponse.uploaded && uploadResponse.fileRef) {
                    const file = mapValues[i]
                    file.percent = 100;
                    (file as any).fileRef = uploadResponse.fileRef;
                    jsonpath.value(payload, mapKeys[i], uploadResponse.fileRef)
                }
            }
        } catch (error) {
            console.error(error)
            throw error
        }
    } else {
        throw new Error('[FORM_COMPOSITOR]: UploadFile function required');
    }
}

const setUploadMap = (file: UploadFile, fieldPrevValue?: string) => {
    if (file.percent === 0) {
        return file
    }
    return fieldPrevValue || (file as any).fileRef
}
const getUploadMap = (payload: FieldDTOPayload, fields: JFieldDTO[], jpath = '$..') => {
    const map: Record<string, UploadFile | string> = {}
    for (let i = 0; i < payload.length; i++) {
        const payloadField = payload[i];
        const field = fields.find(f => f.hurlId === payloadField.hurlId)
        if (isUploadableField(payloadField.type)) {
            if (Array.isArray(payloadField.values)) {
                for (let y = 0; y < payloadField.values.length; y++) {
                    const value = payloadField.values[y];
                    if (isUploadFile(value)) {
                        map[`${jpath}[${i}].values[${y}]`] = setUploadMap(payloadField.values[y], field?.values?.[y])
                    }
                    if (Array.isArray(value)) {
                        for (let x = 0; x < value.length; x++) {
                            const repeatvalue = value[x];

                            if (isUploadFile(repeatvalue)) {
                                map[`${jpath}[${i}].values[${y}][${x}]`] = setUploadMap(repeatvalue, field?.values?.[y]?.[x])

                            }

                        }
                    }
                }
            }
        } else if (payloadField.children?.length && field?.children?.length) {
            const childmap = getUploadMap(payloadField.children, field?.children, `${jpath}[${i}].children`)
            for (const [key, value] of Object.entries(childmap)) {
                map[key] = value
            }
        }
    }
    return map
}
export const buildFieldsPayload = (_output: Output, fields: JFieldDTO[], repeateble?: boolean) => {

    let payload: FieldDTOPayload = []
    for (const field of fields) {
        let outputValues: any[] = []
        const item: FieldDTOItemPayload = {
            hurlId: field.hurlId,
            type: field.type,
            values: []
        }

        if (field.children?.length && field.repeateble) {
            const obj: Record<string, any> = {}
            for (const outputValue of _output[field.hurlId] as any[]) {
                for (const [key, value] of Object.entries(outputValue)) {
                    if (!obj[key]) obj[key] = []
                    obj[key].push(value)
                }
            }
            outputValues.push(obj)
        } else if ((field.repeateble || repeateble) && Array.isArray(_output[field.hurlId])) {
            for (const outputValue of _output[field.hurlId] as any[]) {
                if ((isMultiSelectableField(field.type) || field.children?.length) && ![undefined, null].includes(outputValue)) {
                    outputValues.push([...outputValue])
                } else {
                    outputValues.push([outputValue])
                }
            }
        } else {
            if (isMultiSelectableField(field.type) && Array.isArray(_output[field.hurlId])) {
                outputValues.push(..._output[field.hurlId] as any[])
            } else {
                outputValues.push(_output[field.hurlId])
            }
        }

        for (let i = 0; i < outputValues.length; i++) {
            const outputValue = outputValues[i];
            if (Array.isArray(item.values)) {
                if (field.children?.length) {
                    delete item.values
                    item.children = buildFieldsPayload(outputValue as Output, field.children, field.repeateble)
                } else {
                    item.values = outputValues
                }

            }
        }

        if (Array.isArray(item.values)) {
            item.values = item.values.map(value => {
                if (Array.isArray(value)) value = value.map(v => (v || v === 0) ? v : '')
                return (value || value === 0) ? value : ''
            })
            if (item.values.length === 0) item.values.push('')
        }
        payload.push(item)
    }
    return payload
}

export function isImage(file: File | UploadFile) {
    return ['image/gif', 'image/jpeg', 'image/png'].includes(file?.type || '')
}
export const valueFormatter = (field: JFieldDTO, dateFormats?: { date?: string, datetime?: string }) => {
    const values = field.values || []
    if (isGenericDateField(field.type)) {
        let str = ''
        for (let i = 0; i < values.length; i++) {
            const value = values[i];

            if (isFormattableDate(value)) {
                window.moment = moment
                if (isDateField(field.type) || field.cmsConfig?.componentConfig?.showTime === false) {
                    str += moment(value).format(dateFormats?.date ?? DEFAULT_FORMAT_DATE_FORM)
                } else { // if(isDateTimeField(field.type))
                    str += moment(value).format(dateFormats?.datetime ?? DEFAULT_FORMAT_DATETIME_FORM)
                }
            } else {
                str += value
            }

            if (i !== values.length - 1) {
                str += ' / '
            }
        }
        return str
    }
    if (isSelectableField(field.type) && field.values?.length && field.cmsConfig.componentConfig?.options?.length) {
        return (
            values.map(value => field.cmsConfig.componentConfig.options.find(o => o.value === value)?.label.title || value).join(',')
        )
    }
    return values.join(',')
}
export const isFieldType = {
    TEXT: (type: JFieldDTOType) => ['JTEXT', 'TEXT', 'JINVITATION', 'INVITATION', 
    'JINVITATION_CODE', 'INVITATION_CODE'].includes(type),
    PHONE: (type: JFieldDTOType) => ['JPHONE', 'PHONE'].includes(type),
    COUNTRY: (type: JFieldDTOType) => ['JCOUNTRY', 'COUNTRY'].includes(type),
    SELECT: (type: JFieldDTOType) => ['JSELECT', 'SELECT'].includes(type),
    MULTISELECT: (type: JFieldDTOType) => ['JMULTISELECT', 'MULTISELECT'].includes(type),
    NUMBER: (type: JFieldDTOType) => ['JNUMBER', 'NUMBER'].includes(type),
    EMAIL: (type: JFieldDTOType) => ['JEMAIL', 'EMAIL'].includes(type),
    PASSWORD: (type: JFieldDTOType) => ['JPASSWORD', 'PASSWORD'].includes(type),
    RADIO: (type: JFieldDTOType) => ['JRADIO', 'RADIO'].includes(type),
    CHECKBOX: (type: JFieldDTOType) => ['JCHECKBOX', 'CHECKBOX'].includes(type),
    DATE: (type: JFieldDTOType) => ['JDATE', 'DATE'].includes(type),
    DATES_RANGE: (type: JFieldDTOType) => ['JDATES_RANGE', 'DATES_RANGE', 'JDATERANGE', 'DATERANGE'].includes(type),
    DATETIME: (type: JFieldDTOType) => ['JDATETIME', 'DATETIME'].includes(type),
    DATETIME_RANGE: (type: JFieldDTOType) => ['JDATETIMERANGE', 'DATETIMERANGE'].includes(type),
    AVATAR: (type: JFieldDTOType) => ['JAVATAR', 'AVATAR'].includes(type),
    FILE: (type: JFieldDTOType) => ['JFILE', 'FILE'].includes(type),
    PRIVACY: (type: JFieldDTOType) => ['JPRIVACY', 'PRIVACY'].includes(type),
    TEXTAREA: (type: JFieldDTOType) => ['JTEXTAREA', 'TEXTAREA'].includes(type),
    COMPOSED: (type: JFieldDTOType) => ['JCOMPOSED', 'COMPOSED'].includes(type),
    INVITATION: (type: JFieldDTOType) => ['JINVITATION', 'INVITATION'].includes(type)
}

export const isFormattableDate = (value: string) => !isNaN(Date.parse(value))

export const isUploadableField = (type: JFieldDTOType) => (
    isFieldType.AVATAR(type) ||
    isFieldType.FILE(type)
)
export const isDateField = (type: JFieldDTOType) => (
    isFieldType.DATE(type) ||
    isFieldType.DATES_RANGE(type)
)
export const isDateTimeField = (type: JFieldDTOType) => (
    isFieldType.DATETIME(type) ||
    isFieldType.DATETIME_RANGE(type)
)
export const isGenericDateRangeField = (type: JFieldDTOType) => (
    isFieldType.DATES_RANGE(type) ||
    isFieldType.DATETIME_RANGE(type)
)
export const isGenericDateField = (type: JFieldDTOType) => (
    isFieldType.DATE(type) ||
    isFieldType.DATETIME(type) ||
    isFieldType.DATES_RANGE(type) ||
    isFieldType.DATETIME_RANGE(type)
)
export const isSelectableField = (type: JFieldDTOType) => (
    isFieldType.RADIO(type) ||
    isFieldType.PRIVACY(type) ||
    isFieldType.CHECKBOX(type) ||
    isFieldType.SELECT(type) ||
    isFieldType.MULTISELECT(type)
)
export const isMultiSelectableField = (type: JFieldDTOType) => (
    isFieldType.CHECKBOX(type) ||
    isFieldType.MULTISELECT(type) ||
    isUploadableField(type) ||
    isGenericDateRangeField(type)
)

/**
 * Apply timezone to iso date
 *
 * @export
 * @param {string} value
 * @return {*} 
 */
export function applyTZToISODate(value: string) {
    const date = new Date(value);
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString();
}
export function toISODateString(value: Moment | Date) {
    return value.toISOString().split('T')[0]
}

export function transformMinMaxDateParam(date: ComponentConfigExtended['minDate'] | ComponentConfigExtended['maxDate']) {
    if (!date) return

    if (date === 'TODAY') {
        const d = new Date()
        d.setHours(0, 0, 0, 0)
        return d
    }

    if(date.includes('_YEARS')) {
        const years = parseInt(date.replace('_YEARS', ''));
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        return new Date(currentDate.setFullYear(currentDate.getFullYear() - years))
    }

    return new Date(date)
}
export const disabledDate = (moment: Moment, minDate?: Date, maxDate?: Date) => {
    if (maxDate && minDate) {
        return !moment.isBetween(minDate, maxDate, 'day', "[]");
    } else if (maxDate) {
        return moment.isAfter(maxDate, 'day')
    } else if (minDate) {
        return moment.isBefore(minDate, 'day')
    }
    return false
}
export const disabledRangeDate = (moments: Moment[], minDate?: Date, maxDate?: Date) => {
    for (const moment of moments) {
        if (disabledDate(moment, minDate, maxDate)) {
            return true
        }
    }
    return false
}


export const splitPhoneNumber = (value: string = '') => {
    const prefix = value.match(/\+(.*?)(\s|$)/)?.[0].trim()
    const number = value.replace(`${prefix}`, '').trim()
    return {
        prefix,
        number
    }
} 